/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useRef } from 'react';
import shallow from 'zustand/shallow';

import useVideoPlayerStore, { VideoPlayerActions } from '../store';

const TICK_FREQUENCY = 1000;

interface YouTubeTimerProps {
  onReady: (ready: boolean) => any;
  onDuration: (duration: number) => any;
  onCurrentTime: (time: number) => any;
  onPause: (time: number) => any;
  registerYouTubePlayer: VideoPlayerActions['registerYouTubePlayer'];
}

export default function useYouTubeTimer({ ...props }): Partial<
  Omit<YouTubeTimerProps, 'onReady' | 'onPause'>
> & {
  onReady: (event: YT.PlayerEvent) => void;
  onPause: () => void;
} {
  const {
    onDuration,
    onCurrentTime,
    onPause,
    onReady,
    registerYouTubePlayer,
    onContentState,
    onStreamHost
  } = useVideoPlayerStore(
    state => ({
      onDuration: state.onDuration,
      onPause: state.onPause,
      onReady: state.onReady,
      onCurrentTime: state.onCurrentTime,
      registerYouTubePlayer: state.registerYouTubePlayer,
      onContentState: state.onContentState,
      onStreamHost: state.onStreamHost
    }),
    shallow
  );

  const timings = useRef({ duration: undefined, currentTime: undefined });
  const internalPlayer = useRef<YT.Player>(null);
  const ticker = useRef(null);

  const handleTick = useCallback(() => {
    if (!internalPlayer.current || !ticker.current) return;

    if (internalPlayer.current.getCurrentTime) {
      const currentTime = internalPlayer.current.getCurrentTime();
      timings.current.currentTime = currentTime;
      if (onCurrentTime) {
        onCurrentTime(currentTime);
      }
    }

    if (internalPlayer.current.getDuration) {
      const duration = internalPlayer.current.getDuration();
      if (timings.current.duration !== duration) {
        timings.current.duration = duration;
        if (onDuration) {
          onDuration(duration);
        }
      }
    }
  }, [internalPlayer, onDuration, onCurrentTime, timings]);

  const startTicker = useCallback(() => {
    ticker.current = ticker.current || setInterval(handleTick, TICK_FREQUENCY);
  }, [handleTick]);

  const stopTicker = useCallback(() => {
    if (ticker.current) {
      clearInterval(ticker.current);
      ticker.current = null;
    }
  }, [ticker]);

  const handleReady = useCallback(
    (event: YT.PlayerEvent) => {
      registerYouTubePlayer(event.target);
      timings.current = { duration: undefined, currentTime: undefined };
      internalPlayer.current = event.target;
      onReady(true);
      onStreamHost('youtube.com');
      onContentState('content');
    },
    [onReady, registerYouTubePlayer]
  );

  const handlePause = useCallback(() => {
    onPause(timings.current.currentTime);
  }, [onPause]);

  useEffect(() => {
    startTicker();
    return () => stopTicker();
  }, [startTicker, stopTicker]);

  return {
    ...props,
    onReady: handleReady,
    onDuration,
    onCurrentTime,
    onPause: handlePause
  };
}
